import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Calendar, FileCheck2, DollarSign, RefreshCw, AlertTriangle, TrendingDown, TrendingUp } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CarregandoBloco, CarregandoCards, CarregandoLista } from "@/components/carregando";
import { Input } from "@/components/ui/input";

import BalaoGraficoFiscal from "./BalaoGraficoFiscal";

const API = "/api";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

// Mesmo filtro das outras telas de relatório, com "Este ano" como padrão.
const FILTROS = [
  { key: "mes", label: "Este mês" },
  { key: "mes_passado", label: "Mês passado" },
  { key: "ano", label: "Este ano" },
  { key: "custom", label: "Personalizado" },
];

/** Cor primária do tema, igual ao VisaoGeral — o gráfico segue a identidade da loja. */
function getPrimaryHex() {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
    if (!raw) return "#f97316";
    const [h, s, l] = raw.split(/[\s,/%]+/).map(Number);
    const s1 = s / 100, l1 = l / 100;
    const a = s1 * Math.min(l1, 1 - l1);
    const f = (n) => {
      const k = (n + h / 30) % 12;
      const c = l1 - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
      return Math.round(c * 255).toString(16).padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  } catch {
    return "#f97316";
  }
}

// Anos anteriores em cinza, pra linha do ano atual não competir com nada.
const CINZAS = ["#94a3b8", "#cbd5e1", "#a8a29e", "#d6d3d1"];

// Contagem com ponto de milhar (9.172), igual ao dinheiro — pedido do dono.
const inteiro = (v) => Number(v || 0).toLocaleString("pt-BR");

const brl = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Variacao({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="text-[11px] text-muted-foreground">sem base anterior</span>;
  }
  const sobe = pct >= 0;
  const Icone = sobe ? TrendingUp : TrendingDown;
  return (
    <span
      className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${
        sobe ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
      }`}
    >
      <Icone className="w-3 h-3" />
      {sobe ? "+" : ""}{Number(pct).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
    </span>
  );
}

function CardAnual({ icone: Icone, titulo, cor, linhas, formatar }) {
  return (
    <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b bg-muted/30 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${cor} flex items-center justify-center shrink-0`}>
          <Icone className="w-5 h-5" />
        </div>
        <span className="text-sm font-semibold">{titulo}</span>
      </div>
      {linhas.length === 0 ? (
        <p className="p-5 text-sm text-muted-foreground">Nenhuma nota emitida ainda.</p>
      ) : (
        <div className="divide-y">
          {linhas.map((l) => (
            <div key={l.ano} className="flex items-center justify-between px-5 py-3">
              <span className="text-sm font-mono text-muted-foreground tabular-nums">{l.ano}</span>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold tabular-nums">{formatar(l.valor)}</span>
                <Variacao pct={l.variacao} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ResultadosFiscais() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState("ano");
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");

  const carregar = useCallback(async () => {
    if (filtro === "custom" && (!inicio || !fim)) return;
    setCarregando(true);
    try {
      const params = new URLSearchParams({ filtro });
      if (filtro === "custom") {
        params.set("inicio", inicio);
        params.set("fim", fim);
      }
      const { data } = await axios.get(`${API}/fiscal/resultados?${params}`, auth());
      setDados(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível carregar os resultados");
    } finally {
      setCarregando(false);
    }
  }, [filtro, inicio, fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const primary = useMemo(getPrimaryHex, []);

  // Recharts quer uma linha por mês com uma coluna por ano.
  const serie = useMemo(() => {
    const anos = dados?.grafico || [];
    return MESES.map((mes, i) => {
      const ponto = { mes };
      anos.forEach((a) => { ponto[String(a.ano)] = a.meses[i]; });
      return ponto;
    });
  }, [dados]);

  const anosGrafico = dados?.grafico || [];

  return (
    <div className="space-y-5">
      {/* Filtro de período */}
      <div className="bg-card rounded-xl border p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground mr-2">Período:</span>
          {FILTROS.map((f) => (
            <Button
              key={f.key}
              variant={filtro === f.key ? "default" : "outline"}
              size="sm"
              onClick={() => setFiltro(f.key)}
            >
              {f.label}
            </Button>
          ))}
          {filtro === "custom" && (
            <div className="flex items-center gap-2 ml-1">
              <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className="h-9 w-auto text-sm" />
              <span className="text-xs text-muted-foreground">até</span>
              <Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className="h-9 w-auto text-sm" />
            </div>
          )}
          <button
            type="button"
            onClick={carregar}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm hover:bg-muted/50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${carregando ? "animate-spin" : ""}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Carregamento com cards (25/10/2026): só na primeira carga, no lugar de
          "Nenhuma nota emitida ainda" antes da hora */}
      {carregando && !dados ? (
        <>
          <CarregandoCards quantos={2} className="grid grid-cols-1 sm:grid-cols-2 gap-3" altura="h-[86px]" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CarregandoLista linhas={3} avatar={false} colunas={1} />
            <CarregandoLista linhas={3} avatar={false} colunas={1} />
          </div>
          <CarregandoBloco altura="h-[400px]" />
        </>
      ) : (
      <>
      {/* Resumo do período escolhido */}
      {dados?.periodo && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-card rounded-xl border p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Notas no período</p>
            <p className="text-2xl font-bold tabular-nums mt-1">{inteiro(dados.periodo.notas)}</p>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Valor no período</p>
            <p className="text-2xl font-bold tabular-nums mt-1">{brl(dados.periodo.valor)}</p>
          </div>
        </div>
      )}

      {dados?.modo_teste && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900">
          <FileCheck2 className="w-5 h-5 text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold text-sky-700 dark:text-sky-300">
              Mostrando notas de teste ({dados.modo_teste === "simulacao" ? "Simulação" : "Homologação"}) — sem valor fiscal.
            </span>
            <p className="text-[11px] text-sky-700/80 dark:text-sky-400/80 mt-0.5">
              Enquanto a loja está em teste, os números e gráficos usam as notas de teste. Quando ela
              passar para Produção, aqui entram só as notas valendo.
            </p>
          </div>
        </div>
      )}

      {!dados?.modo_teste && dados?.notas_teste?.notas > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900">
          <FileCheck2 className="w-5 h-5 text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold text-sky-700 dark:text-sky-300">
              {inteiro(dados.notas_teste.notas)} {dados.notas_teste.notas === 1 ? "nota de teste" : "notas de teste"} ·{" "}
              {brl(dados.notas_teste.valor)}
            </span>
            <p className="text-[11px] text-sky-700/80 dark:text-sky-400/80 mt-0.5">
              Emitidas em Simulação ou Homologação — sem valor fiscal. Aparecem aqui para você
              conferir o teste, mas não entram nos números abaixo.
            </p>
          </div>
        </div>
      )}

      {dados?.canceladas_fora_do_prazo > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold text-amber-700 dark:text-amber-300">
              {inteiro(dados.canceladas_fora_do_prazo)} nota(s) de pedidos cancelados continuam valendo.
            </span>
            <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-0.5">
              O pedido foi cancelado depois de 28 minutos da emissão, quando a SEFAZ não aceita
              mais o cancelamento. A nota segue valendo e entra no resultado.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CardAnual
          icone={FileCheck2}
          titulo="Notas emitidas por ano"
          cor="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
          linhas={dados?.notas_por_ano || []}
          formatar={(v) => `${inteiro(v)} ${v === 1 ? "nota" : "notas"}`}
        />
        <CardAnual
          icone={DollarSign}
          titulo="Valor emitido por ano"
          cor="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400"
          linhas={dados?.valor_por_ano || []}
          formatar={brl}
        />
      </div>

      <div className="bg-card rounded-2xl border shadow-sm p-5">
        <p className="text-sm font-semibold mb-1">Valor emitido ao longo do ano</p>
        <p className="text-[11px] text-muted-foreground mb-4">
          A linha destacada é o ano atual. Anos sem emissão não aparecem.
        </p>
        {anosGrafico.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Sem notas emitidas para montar o gráfico.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={serie} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={70}
                tickFormatter={(v) => `R$ ${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`}
              />
              <Tooltip content={<BalaoGraficoFiscal formatar={brl} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {anosGrafico.map((a, i) => (
                <Line
                  key={a.ano}
                  type="monotone"
                  dataKey={String(a.ano)}
                  name={String(a.ano)}
                  stroke={a.principal ? primary : CINZAS[i % CINZAS.length]}
                  strokeWidth={a.principal ? 3 : 1.5}
                  dot={false}
                  activeDot={{ r: a.principal ? 5 : 3 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      </>
      )}
    </div>
  );
}
